import React, { useEffect, useMemo, useState } from 'react';
import { authAPI } from './api';


const apiErrorMessage = (error, fallback) => {
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  return fallback;
};


export default function Register({ onRegister, setCurrentView }) {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: ''
  });
  const [consents, setConsents] = useState([]);
  const [accepted, setAccepted] = useState({});
  const [error, setError] = useState('');
  const [loadingConsents, setLoadingConsents] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;

    authAPI.getRegistrationConsents()
      .then((response) => {
        if (active) setConsents(response.data);
      })
      .catch((requestError) => {
        if (active) {
          setError(apiErrorMessage(
            requestError,
            'The required account terms could not be loaded. Please try again.'
          ));
        }
      })
      .finally(() => {
        if (active) setLoadingConsents(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const requiredConsents = useMemo(
    () => consents.filter((consent) => consent.required),
    [consents]
  );
  const allRequiredAccepted = requiredConsents.length > 0
    && requiredConsents.every((consent) => accepted[consent.consent_id] === true);

  const handleChange = (event) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value
    });
  };

  const handleConsentChange = (consentId) => {
    setAccepted((current) => ({
      ...current,
      [consentId]: !current[consentId]
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!allRequiredAccepted) {
      setError('You must separately accept all three required statements.');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      const { confirmPassword, ...accountFields } = formData;
      await authAPI.register({
        ...accountFields,
        accepted_consents: requiredConsents.map((consent) => ({
          consent_id: consent.consent_id,
          version: consent.version
        }))
      });

      const loginResponse = await authAPI.login({
        email: formData.email,
        password: formData.password
      });

      localStorage.setItem('access_token', loginResponse.data.access_token);
      onRegister(loginResponse.data.user);
    } catch (requestError) {
      setError(apiErrorMessage(
        requestError,
        'Registration failed. Please try again.'
      ));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 sm:p-10">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-900 rounded-full flex items-center justify-center mx-auto">
              <span className="text-2xl font-bold text-white">GPJ</span>
            </div>
            <h1 className="mt-6 text-3xl font-bold text-gray-900">Create your account</h1>
            <p className="mt-2 text-gray-600">
              Review and separately accept each required statement before creating your account.
            </p>
          </div>

          <form className="space-y-8" onSubmit={handleSubmit}>
            {error && (
              <div role="alert" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <fieldset className="space-y-4">
              <legend className="text-lg font-semibold text-gray-900">Your account details</legend>
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block text-sm font-medium text-gray-700">
                  First name
                  <input name="first_name" type="text" required value={formData.first_name} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md" />
                </label>
                <label className="block text-sm font-medium text-gray-700">
                  Last name
                  <input name="last_name" type="text" required value={formData.last_name} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md" />
                </label>
              </div>
              <label className="block text-sm font-medium text-gray-700">
                Email address
                <input name="email" type="email" required value={formData.email} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md" />
              </label>
              <label className="block text-sm font-medium text-gray-700">
                Phone number (optional)
                <input name="phone" type="tel" value={formData.phone} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md" />
              </label>
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block text-sm font-medium text-gray-700">
                  Password
                  <input name="password" type="password" minLength="8" required value={formData.password} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md" />
                </label>
                <label className="block text-sm font-medium text-gray-700">
                  Confirm password
                  <input name="confirmPassword" type="password" minLength="8" required value={formData.confirmPassword} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md" />
                </label>
              </div>
            </fieldset>

            <fieldset className="space-y-5" aria-describedby="consent-help">
              <legend className="text-lg font-semibold text-gray-900">Required acknowledgements</legend>
              <p id="consent-help" className="text-sm text-gray-600">
                Each checkbox requires its own selection. None are selected for you.
              </p>

              {loadingConsents && <p role="status" className="text-gray-600">Loading required statements…</p>}

              {consents.map((consent) => (
                <article key={`${consent.consent_id}:${consent.version}`} className="border border-gray-200 rounded-xl p-5 text-left">
                  <h2 className="font-semibold text-blue-950">{consent.title}</h2>
                  <p className="mt-2 text-sm leading-6 text-gray-700">{consent.text}</p>
                  <label className="mt-4 flex gap-3 items-start font-medium text-gray-900">
                    <input
                      type="checkbox"
                      name={`consent-${consent.consent_id}`}
                      checked={accepted[consent.consent_id] === true}
                      onChange={() => handleConsentChange(consent.consent_id)}
                      className="mt-1 h-5 w-5"
                    />
                    <span>{consent.checkbox_text}</span>
                  </label>
                </article>
              ))}
            </fieldset>

            <button
              type="submit"
              disabled={loadingConsents || submitting || !allRequiredAccepted}
              className="w-full flex justify-center py-3 px-4 rounded-md text-white bg-blue-900 hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Creating account…' : 'Create Account'}
            </button>

            <div className="text-center">
              <button type="button" onClick={() => setCurrentView('login')} className="text-blue-700 hover:text-blue-600 text-sm">
                Already have an account? Sign in
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
